import type { JSX } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { AnimationControls } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import { debounce, isEmpty, noop } from 'lodash';
import type { Membership, Role } from '@frameio/graphql-client/generated';
import { useGetResourcesGrantingAccessQuery } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useGetActiveUserId } from '@frameio/shared/hooks/useGetActiveUserId';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import { Box, theme } from '@frameio/vapor';
import { useDeleteGuestMembership } from '../../hooks/useDeleteGuestMembership';
import { useDeleteMembership } from '../../hooks/useDeleteMembership';
import { useDeleteWithAccessCheck } from '../../hooks/useDeleteWithAccessCheck';
import { useGetDirectMemberships } from '../../hooks/useGetDirectMemberships';
import { useIsRestrictedFolder } from '../../hooks/useIsRestrictedFolder';
import { useResourceName } from '../../hooks/useResourceName';
import { FolderAccessToggle } from '../FolderAccessToggle';
import { GuestRemovalConfirmationDialog } from '../GuestRemovalConfirmationDialog';
import { ProjectAccessToggle } from '../ProjectAccessToggle';
import TEXT from './generated/TEXT/InviteAccessMembers';
import {
  type StateActions,
  useListAnimationState,
} from './hooks/useListAnimationState';
import { InviteAccessMemberList } from './InviteAccessMember/InviteAccessMemberList';
import { InviteAccessResourceList } from './InviteAccessResource/InviteAccessResourceList';
import { InviteScrollWrapper } from './InviteScrollWrapper';
import { NoDirectMembers } from './NoDirectMembers';
import type {
  handleMembershipDeletionArgs,
  InviteAccessMemberProps,
} from './types';
import { useRowAnimationControls } from './useRowAnimationControls';

const INITIAL_ROW_STYLE = {
  backgroundColor: theme.colors.background.interactive.highlight,
  height: '0px',
  opacity: 0,
};

TEXT.DEFINE({
  /** Header for subgroup list of members with inherited/folder access to a resource. */
  inheritedHeader: (isFolder: boolean = false) =>
    isFolder ? 'Folder Access' : 'Inherited access',

  /** Header for subgroup list of members with direct access to a resource. */
  directHeader: (isFolder: boolean = false) =>
    isFolder ? 'Direct members' : 'Direct access',

  /** Label for members button showing icon and count */
  buttonLabel: (Icon: any, count: any) => (
    <>
      Members {Icon} {count}
    </>
  ),

  /** Label for invite users button */
  inviteButtonLabel: 'Invite users',

  accessListHeader: {
    /** Header showing folder user count */
    folder: (count: number) => `Folder users (${count})`,

    /** Header showing project user count */
    project: (count: number) => `Project users (${count})`,

    /** Header showing workspace user count */
    workspace: (count: number) => `Workspace users (${count})`,
  },

  accessCategoryIconAriaLabel: {
    /** Aria label for folder access icon */
    folder: 'Folder',

    /** Aria label for project access icon */
    project: 'Project',

    /** Aria label for workspace access icon */
    workspace: 'Workspace',
  },

  /** Toast message confirming member removal with names */
  memberRemoved: (resourceName: string, memberName: string) =>
    `${memberName} was removed from ${resourceName}`,

  /** Aria label for search loading spinner */
  spinnerLabel: 'Searching',

  /** Label for undo button in toast */
  undo: 'Undo',
});

type Props = {
  accessCanBeManaged?: boolean;
  animateControls?: AnimationControls;
  adornmentAfterSearch?: JSX.Element;
  deferRender?: boolean;
  disableResourceActions?: boolean;
  fetchData?: boolean;
  focusSearchOnMount?: boolean;
  onResourceHasMutated?: () => void;
  resourceId: string;
  availableRoles?: Array<Role>;
  onChangeResource?: (id: string) => void;
  placeholderCount?: number;
  refetchQueries?: Parameters<typeof useDeleteMembership>[0]['refetchQueries'];
  resourceType: string;
  scrollHeight?: string;
  isRestrictedProject?: boolean;
  trackingSource?: TrackingSourceType;
} & Pick<InviteAccessMemberProps, 'backgroundColor'>;

const inheritedListAnimation = {
  initial: {
    opacity: 0,
    height: 0,
    overflow: 'hidden',
  },
  animate: {
    opacity: 1,
    height: 'auto',
    transition: {
      duration: 0.3,
      delay: 0.1,
      ease: theme.transitions.curves.primary,
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.3,
      ease: theme.transitions.curves.primary,
    },
  },
};

export const InviteAccessMembers = forwardRef<HTMLDivElement | null, Props>(
  (
    {
      accessCanBeManaged = false,
      animateControls,
      adornmentAfterSearch,
      backgroundColor = 'secondary',
      deferRender,
      disableResourceActions,
      fetchData,
      focusSearchOnMount,
      resourceId,
      availableRoles,
      onChangeResource,
      onResourceHasMutated,
      placeholderCount = 0,
      refetchQueries,
      resourceType,
      scrollHeight = '100%',
      isRestrictedProject = false,
      trackingSource,
    },
    focusRef
  ) => {
    const activeUserId = useGetActiveUserId();
    const [loadingIsSlow, setLoadingIsSlow] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
    const { animationState: state, animationStateDispatch: dispatch } =
      useListAnimationState();
    const { animateRowIn, animateRowOut, rowControls } =
      useRowAnimationControls();
    const onCompleteTimeout = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );

    const accountId = useActiveAccountId();

    const {
      data: resourcesGrantingAccessData,
      loading: isResourcesGrantingAccessLoading,
    } = useGetResourcesGrantingAccessQuery({
      variables: {
        accountId: accountId ?? '',
        id: resourceId,
        page: { first: DEFAULT_INITIAL_PAGE_SIZE },
      },
      skip: !fetchData || !resourceId || !accountId,
    });

    const {
      data: directMembershipsData,
      directMemberships,
      indirectMemberships,
      fetchNextPage,
      loading: isDirectMembershipsLoading,
    } = useGetDirectMemberships({
      fetchData: fetchData && Boolean(resourceId),
      resourceId,
      searchTerm,
      onCompleted: (results) => {
        const noResults = [
          !results?.directMemberships?.nodes.length,
          !results?.indirectMemberships?.nodes.length,
          searchTerm,
        ].every(Boolean);
        let action: StateActions | undefined;
        if (searchTerm && !noResults) {
          action = 'searchedAndReceivedResults';
        }
        if (searchTerm && noResults) {
          action = 'searchedAndDidNotReceiveResults';
        }
        if (!searchTerm) {
          action = 'queriedWithoutSearchTerm';
        }

        if (action) {
          dispatch(action);
        }
      },
    });

    const loading =
      isDirectMembershipsLoading || isResourcesGrantingAccessLoading;
    const isFirstTimeLoading =
      loading && (!directMembershipsData || !resourcesGrantingAccessData);

    const handleRemoveRow = async (id: string) => {
      dispatch('removeMemberRow');
      await animateRowOut(id);
      dispatch('animationCompleted');
    };

    const deleteWithAccessCheck = useDeleteWithAccessCheck({
      refetchQueries,
      resourceId,
      resourceType,
      onBeginMutation: async (type, id) => {
        if (type === 'delete') {
          handleRemoveRow(id);
        }
        if (type === 'undoDelete') {
          dispatch('addMemberRow');
        }
      },
      onCompleteMutation: async (type, membership) => {
        onResourceHasMutated?.();
        if (type === 'undoDelete' && membership) {
          handleMembershipRestore(membership.id);
        }
      },
    });

    /**
     * Handles the restoration of a previously deleted membership
     * Waits for DOM to render before applying animation
     */
    const handleMembershipRestore = (id: string) => {
      /**
       * Wait a tick to allow dom to render first
       * We can't apply an animation to the row until it exists in the DOM
       * Without waiting a tick, there's no Element and therefore nothing
       * with the animation hook attached to it. Could there be a better way
       * to accomplish this? Waiting for an effect to fire could end up
       * briefly rendering the row prior to animating it in and add an
       * additional re-render
       */
      if (onCompleteTimeout) {
        // Ensure the state update is complete before setting the timeout
        dispatch('addMemberRow');
        onCompleteTimeout.current = setTimeout(async () => {
          // Check if the element is present in the DOM
          const element = document.getElementById(id);
          if (element) {
            await animateRowIn(id);
            dispatch('animationCompleted');
          }
        }, 0);
      }
    };

    const resourceName = useResourceName(resourceId, resourceType);
    const { deleteMembership } = useDeleteMembership({
      onBeginMutation: async (type, id) => {
        if (type === 'delete') {
          handleRemoveRow(id);
        }
      },
      onCompleteMutation: async () => {
        onResourceHasMutated?.();
      },
      resourceId,
      resourceName: resourceName ?? '',
    });
    const deleteGuestMembership = useDeleteGuestMembership({
      resourceId,
      resourceType,
      onBeginMutation: handleRemoveRow,
      onCompleteMutation: onResourceHasMutated,
    });

    const handleMembershipDeletion = useCallback(
      ({ membershipId, memberName }: handleMembershipDeletionArgs) => {
        const membership = directMemberships?.nodes?.find(
          (node) => node.id === membershipId
        );
        if (membership?.accountUser?.licenseType === 'GUEST') {
          return deleteGuestMembership({
            membershipId,
            memberName,
            refetchQueries,
          });
        }
        const isCurrentUser = membership?.accountUser?.userId === activeUserId;
        if (isCurrentUser) {
          return deleteWithAccessCheck({
            membershipId,
            memberName,
            refetchQueries: isCurrentUser ? [] : refetchQueries,
          });
        }
        return deleteMembership({
          membershipId,
          memberName,
          refetchQueries,
        });
      },
      [
        activeUserId,
        deleteGuestMembership,
        deleteWithAccessCheck,
        deleteMembership,
        directMemberships?.nodes,
        refetchQueries,
      ]
    );

    const memoizedDebounce = useMemo(() => {
      return debounce(() => setLoadingIsSlow(true), 300);
    }, []);

    useEffect(() => {
      if (focusRef && 'current' in focusRef && focusSearchOnMount) {
        focusRef?.current?.focus();
      }
    }, [focusRef, focusSearchOnMount]);

    useEffect(() => {
      const timeout = onCompleteTimeout.current;
      if (loading) {
        memoizedDebounce();
      } else {
        memoizedDebounce.cancel();
        setLoadingIsSlow(false);
      }
      return () => {
        if (timeout !== null) clearTimeout(timeout);
        memoizedDebounce.cancel();
      };
    }, [loading, memoizedDebounce]);

    const [guestRemovalDialogParams, setGuestRemovalDialogParams] = useState<{
      label: string;
      membershipId: Membership['id'];
    } | null>(null);

    const isProject = resourceType === 'Project';
    const isFolderAsset = resourceType === 'FolderAsset';
    const isRestrictedFolder = useIsRestrictedFolder(
      resourceId,
      !isFolderAsset
    );
    const shouldHideIndirectMembers =
      (isProject && isRestrictedProject) ||
      (isFolderAsset && isRestrictedFolder);
    const showNoDirectMembers =
      !state.hasActiveSearchTerm &&
      directMembershipsData?.directMemberships?.nodes?.length === 0;
    const shouldShowInviteAccessList =
      (!state.hasActiveSearchTerm &&
        Boolean(resourcesGrantingAccessData) &&
        isProject &&
        !isRestrictedProject) ||
      (isFolderAsset && !isRestrictedFolder);

    return (
      <>
        <GuestRemovalConfirmationDialog
          isShowing={!isEmpty(guestRemovalDialogParams)}
          name={guestRemovalDialogParams?.label ?? ''}
          onConfirm={() =>
            deleteGuestMembership({
              membershipId: guestRemovalDialogParams?.membershipId ?? '',
              memberName: guestRemovalDialogParams?.label ?? '',
            })
          }
          onRequestClose={() => setGuestRemovalDialogParams(null)}
          zIndex={theme.zIndices.modal + 1}
        />
        <InviteScrollWrapper
          ref={focusRef}
          adornmentAfterSearch={adornmentAfterSearch}
          animateControls={animateControls}
          deferRender={deferRender}
          fetchNextPage={fetchNextPage}
          isFirstTimeLoading={isFirstTimeLoading}
          loading={loading}
          loadingIsSlow={loadingIsSlow}
          placeholderCount={placeholderCount}
          scrollHeight={scrollHeight}
          searchTerm={searchTerm ?? ''}
          setSearchTerm={(term: string) => setSearchTerm(term)}
          state={state}
        >
          {isProject && (
            <Box paddingBottom={12} paddingTop={8} paddingX={16}>
              <ProjectAccessToggle
                isProjectPrivate={isRestrictedProject}
                projectId={resourceId}
              />
            </Box>
          )}
          {isFolderAsset && (
            <Box paddingBottom={12} paddingTop={8} paddingX={16}>
              <FolderAccessToggle folderId={resourceId} />
            </Box>
          )}
          <AnimatePresence initial={false} mode="wait">
            {shouldShowInviteAccessList && (
              <motion.div
                key={`inherited-list-${resourceId}-${shouldShowInviteAccessList}`}
                animate="animate"
                exit="exit"
                initial="initial"
                variants={inheritedListAnimation}
              >
                <InviteAccessResourceList
                  backgroundColor={backgroundColor}
                  disableResourceActions={disableResourceActions}
                  disableStickyHeader={loading}
                  onChangeResource={onChangeResource}
                  resourceId={resourceId}
                  resourceTypename={
                    resourcesGrantingAccessData?.account?.resource
                      ?.__typename ?? ''
                  }
                  title={TEXT.inheritedHeader(isFolderAsset) as string}
                  trackingSource={trackingSource}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Indirect members list */}
          {state.hasActiveSearchTerm &&
            (indirectMemberships?.nodes?.length ?? 0) > 0 &&
            !shouldHideIndirectMembers && (
              <InviteAccessMemberList
                accessMembers={indirectMemberships?.nodes ?? []}
                availableRoles={availableRoles}
                backgroundColor={backgroundColor}
                createMemberships={noop}
                deleteMembership={handleMembershipDeletion}
                disableStickyHeader={loading}
                initialRowStyle={
                  state.isAnimatingImperatively && INITIAL_ROW_STYLE
                }
                placeholderCount={placeholderCount}
                queryText={searchTerm}
                resourceId={resourceId}
                resourceType={resourceType}
                title={TEXT.inheritedHeader(isFolderAsset) as string}
                trackingSource={trackingSource}
              />
            )}

          {/* Direct members list */}
          {directMemberships?.nodes?.length &&
          directMemberships.nodes.length > 0 ? (
            <motion.div
              key={`direct-list-${resourceId}-${state.hasActiveSearchTerm}`}
              animate={{
                opacity: 1,
                transition: {
                  duration: 0.3,
                  ease: theme.transitions.curves.primary,
                },
              }}
              exit={{
                opacity: 0,
                transition: {
                  duration: 0.3,
                  ease: theme.transitions.curves.primary,
                },
              }}
              initial={{ opacity: 0 }}
            >
              <InviteAccessMemberList
                accessCanBeManaged={accessCanBeManaged}
                accessMembers={directMemberships?.nodes}
                animateControls={rowControls}
                availableRoles={availableRoles}
                backgroundColor={backgroundColor}
                createMemberships={noop}
                deleteMembership={handleMembershipDeletion}
                disableStickyHeader={loading}
                initialRowStyle={
                  state.isAnimatingImperatively && INITIAL_ROW_STYLE
                }
                placeholderCount={placeholderCount}
                queryText={searchTerm || undefined}
                resourceId={resourceId}
                resourceType={resourceType}
                title={TEXT.directHeader(isFolderAsset) as string}
                trackingSource={trackingSource}
              />
            </motion.div>
          ) : (
            <NoDirectMembers shouldShow={showNoDirectMembers} />
          )}
        </InviteScrollWrapper>
      </>
    );
  }
);
